import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';

// Data layer for the storefront. Retail stores load their whole (small) catalogue once and
// filter in the browser; eYantra's catalogue is ~900 products (4.6 MB with image links), so
// listings page from the server and everything else fetches by id, sharing one cache.
//
// Cached entries expire before the 5-minute presigned image links do
// (aws.s3.presigned-url-expiration-minutes), so a cached thumbnail never outlives its signature.
const TTL_MS = 4 * 60 * 1000;

/** Retail components read `brandName`; the main catalogue calls it `brand`. */
export const normalize = (p) => (p ? { ...p, brandName: p.brandName ?? p.brand ?? null } : p);

const byId = new Map(); // id -> { at, product }
const fresh = (entry) => entry && Date.now() - entry.at < TTL_MS;
const remember = (products) => {
  const at = Date.now();
  products.forEach(p => byId.set(String(p.id), { at, product: p }));
  return products;
};

const memo = new Map(); // request key -> { at, promise }
function cached(key, load) {
  const hit = memo.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  const promise = load();
  promise.catch(() => memo.delete(key));
  memo.set(key, { at: Date.now(), promise });
  return promise;
}

// Sort keys the listing offers -> the API's Pageable sort. Price sorts use the pre-GST selling
// price column (the GST-inclusive one is computed, not sortable); the order is the same.
export const SORTS = {
  featured:     { label: 'Featured',           sort: 'id,desc' },
  newest:       { label: 'Newest',             sort: 'id,desc' },
  'price-asc':  { label: 'Price: low to high', sort: 'marketOperatingPrice,asc' },
  'price-desc': { label: 'Price: high to low', sort: 'marketOperatingPrice,desc' },
  'name-asc':   { label: 'Name: A to Z',       sort: 'name,asc' },
  // On-sale products only, biggest discount first — the header's "Deals". The API orders these
  // itself (?deals=true), so there's no Pageable sort.
  discount:     { label: 'Biggest discount',   deals: true },
};

/** One page of products. filters: { category, subcategory, brand, q, min, max, sort }. */
export function fetchProducts({ category, subcategory, brand, q, min, max, sort = 'featured' } = {}, page = 0, size = 24) {
  const spec = SORTS[sort] || SORTS.featured;
  const params = spec.deals ? { page, size, deals: true } : { page, size, sort: spec.sort };
  if (category && category !== 'All') params.category = category;
  if (subcategory) params.subcategory = subcategory;
  if (brand) params.brand = brand;
  if (q) params.search = q;
  if (min) params.minPrice = min;
  if (max) params.maxPrice = max;
  const key = JSON.stringify(params);
  return cached(key, () => api.get('/products', { params }).then(r => {
    const content = remember((r.data?.content || []).map(normalize));
    return { products: content, total: r.data?.totalElements ?? content.length, last: r.data?.last ?? true };
  }));
}

export function fetchProduct(id) {
  const hit = byId.get(String(id));
  if (fresh(hit)) return Promise.resolve(hit.product);
  return api.get(`/products/${id}`).then(r => remember([normalize(r.data)])[0]);
}

/** Products for a list of ids (bag, wishlist, recently viewed), from the cache where possible. */
export function fetchByIds(ids) {
  const wanted = [...new Set(ids.map(String))];
  const missing = wanted.filter(id => !fresh(byId.get(id)));
  const load = missing.length
    ? api.get('/products/by-ids', { params: { ids: missing.join(',') } }).then(r => remember((r.data || []).map(normalize)))
    : Promise.resolve([]);
  return load.then(() => wanted.map(id => byId.get(id)?.product).filter(Boolean));
}

/** { products, loading } for the given ids, kept in the order given. */
export function useProductsByIds(ids) {
  const key = ids.map(String).join(',');
  const [state, setState] = useState({ key: null, products: [], loading: ids.length > 0 });
  useEffect(() => {
    if (!key) { setState({ key, products: [], loading: false }); return undefined; }
    let alive = true;
    setState(s => ({ ...s, loading: true }));
    fetchByIds(key.split(','))
      .then(products => alive && setState({ key, products, loading: false }))
      .catch(() => alive && setState(s => ({ ...s, loading: false })));
    return () => { alive = false; };
  }, [key]);
  return state;
}

/** One page of products for a filter set — rails on the home and product pages. null = none. */
export function useProducts(filters, size = 12) {
  const key = filters ? JSON.stringify(filters) : '';
  const [state, setState] = useState({ products: [], total: 0, loading: !!filters });
  useEffect(() => {
    if (!key) { setState({ products: [], total: 0, loading: false }); return undefined; }
    let alive = true;
    fetchProducts(JSON.parse(key), 0, size)
      .then(r => alive && setState({ ...r, loading: false }))
      .catch(() => alive && setState({ products: [], total: 0, loading: false }));
    return () => { alive = false; };
  }, [key, size]);
  return state;
}

/** Categories, biggest first: [{ name, count, image, subcategories: [names] }].
 *  Two sources: /categories (small, fast — names and types) and /products/category-preview
 *  (counts and a sample photo, but ~400 KB). The names show as soon as the first arrives; the
 *  list is kept for the session so later pages draw the header at once. */
const CATEGORY_CACHE = 'eyantra_categories';
const shape = (cats, preview) => {
  const counts = new Map(preview.map(c => [c.categoryName, c.productCount]));
  const images = new Map(preview.map(c => [c.categoryName, c.sampleProducts?.[0]?.thumbnailUrl || null]));
  const list = cats.map(c => ({
    name: c.name,
    count: counts.get(c.name) ?? null,
    image: images.get(c.name) || null,
    subcategories: (c.subcategories || []).map(s => s.name),
  }));
  return preview.length ? list.filter(c => c.count > 0).sort((a, b) => b.count - a.count) : list;
};
const readCachedCategories = () => {
  try { return JSON.parse(sessionStorage.getItem(CATEGORY_CACHE)) || null; } catch { return null; }
};

export function loadCategories(onNames) {
  return cached('categories', () => {
    const names = api.get('/categories').then(r => r.data || []);
    names.then(cats => onNames?.(shape(cats, []))).catch(() => {});
    return Promise.all([names, api.get('/products/category-preview').then(r => r.data || []).catch(() => [])])
      .then(([cats, preview]) => {
        const list = shape(cats, preview);
        // Photo links are short-lived, so the session copy keeps names and counts only.
        try { sessionStorage.setItem(CATEGORY_CACHE, JSON.stringify(list.map(c => ({ ...c, image: null })))); } catch { /* storage blocked */ }
        return list;
      });
  });
}

export function useCategories() {
  const [categories, setCategories] = useState(() => readCachedCategories() || []);
  useEffect(() => {
    let alive = true;
    loadCategories(names => alive && setCategories(cur => (cur.length ? cur : names)))
      .then(c => alive && setCategories(c)).catch(() => {});
    return () => { alive = false; };
  }, []);
  return categories;
}

/** Brands sold in a category (all brands when no category). */
export function useBrands(category) {
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = category && category !== 'All'
      ? cached(`brands:${category}`, () => api.get('/products/brands-by-category', { params: { category } }).then(r => r.data || []))
      // Every brand in the store (not the top 10 the endpoint gives by default), biggest first —
      // the same request as the Brands menu and page (useTopBrands), so it's fetched once.
      : cached('brands:top:500', topBrandsRequest(500)).then(list => list.map(b => b.brandName));
    load.then(b => alive && setBrands(b)).catch(() => alive && setBrands([]));
    return () => { alive = false; };
  }, [category]);
  return brands;
}

const topBrandsRequest = (limit) => () => api.get('/products/brands/top', { params: { limit, approved: true } }).then(r => r.data || []);

/** Brands by number of products in the store, with logos: the header's Brands menu (a short
 *  list) and the A–Z page (all of them). [{ brandName, productCount, logoUrl }] */
export function useTopBrands(limit) {
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    let alive = true;
    cached(`brands:top:${limit}`, topBrandsRequest(limit))
      .then(b => alive && setBrands(b)).catch(() => alive && setBrands([]));
    return () => { alive = false; };
  }, [limit]);
  return brands;
}

/** Categories (with their types) that have products for a listing narrowed to a brand, a search
 *  or Deals — so its tabs only lead somewhere. null while loading, or when `scope` is null (an
 *  unnarrowed listing uses the full category list). [{ name, count, subcategories: [names] }] */
export function useCategoryCounts(scope) {
  const key = scope ? JSON.stringify(scope) : null;
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!key) { setCounts(null); return undefined; }
    let alive = true;
    setCounts(null);
    const { brand, q, deals } = JSON.parse(key);
    const params = {};
    if (brand) params.brand = brand;
    if (q) params.search = q;
    if (deals) params.deals = true;
    cached(`catcounts:${key}`, () => api.get('/products/category-counts', { params }).then(r => (r.data || []).map(c => ({
      name: c.category, count: c.count, subcategories: (c.subcategories || []).map(s => s.name),
    }))))
      .then(c => alive && setCounts(c)).catch(() => alive && setCounts([]));
    return () => { alive = false; };
  }, [key]);
  return counts;
}

/** MRP range for the current filters — what the API's price filter applies to. */
export function usePriceRange({ category, subcategory, brand, q }) {
  const params = useMemo(() => {
    const p = {};
    if (category && category !== 'All') p.category = category;
    if (subcategory) p.subcategory = subcategory;
    if (brand) p.brand = brand;
    if (q) p.search = q;
    return p;
  }, [category, subcategory, brand, q]);
  const [range, setRange] = useState(null);
  useEffect(() => {
    let alive = true;
    cached(`range:${JSON.stringify(params)}`, () => api.get('/products/price-range', { params }).then(r => r.data))
      .then(r => alive && setRange(r ? [Math.floor(Number(r.minPrice) || 0), Math.ceil(Number(r.maxPrice) || 0)] : null))
      .catch(() => alive && setRange(null));
    return () => { alive = false; };
  }, [params]);
  return range;
}

/** Instant search suggestions (debounced by the caller). */
export function searchSuggestions(q) {
  return fetchProducts({ q }, 0, 6).then(r => r.products);
}
